import BILLING_API from 'api/billing/v1'
import GridSpinner from 'components/UI/GridSpinner'
import PageHeader from 'components/PageHeader'
import { Button, Container, Grid, Icon, NetworkIcon, Typography } from 'gu-ui-kit'
import RefundForm from 'modules/Forms/Billing/RefundForm'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Trans, useTranslation } from 'react-i18next'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router'
import validator from 'utils/asyncValidator'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import styles from 'modules/Services/Refund/refund.module.scss'
import validationSchema from 'modules/Services/Refund/validationSchema'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { FETCH_NETWORK_ACCOUNT } from 'modules/Services/types'
import { FETCH as FETCH_TICKETS, PANEL_NAVIGATE } from 'modules/Tickets/types'

const Refund = () => {
  const history = useHistory()
  const {
    params: { networkType, accountId },
  } = useRouteMatch()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { cancelableDispatch } = useCancelToken()
  const [maxRefundAmount, setMaxRefundAmount] = useState()
  const pathGen = useGeneratePathI18n()

  const { login, office_url } = useSelector(
    (state) => ({
      login: state['services/network-account'].login,
      office_url: state['services/network-account'].office_url,
    }),
    shallowEqual,
  )

  useEffect(() => {
    cancelableDispatch({
      type: FETCH_NETWORK_ACCOUNT,
      payload: { accountId },
    })
  }, [])

  useEffect(() => {
    BILLING_API.calculateRefund({ accountId, data: { amount: null } })
      .then(({ response, error }) => {
        if (error) {
          throw error
        }

        setMaxRefundAmount(response.data.max_refund_amount)
      })
      .catch(() =>
        history.push(
          pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT, {
            networkType,
            accountId,
          }),
        ),
      )
  }, [])

  const onClose = () =>
    history.push(
      pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT, {
        networkType,
        accountId,
      }),
    )

  return (
    <>
      <Helmet defer={false}>
        <title>{t('common::nav_menu.network_account_refund', { login })}</title>
      </Helmet>

      {networkType && accountId && (
        <>
          <Container
            className={styles['banner']}
            display={'flex'}
          >
            <div className={styles['banner__emoji-container']}>
              <Icon name={'emoji-fingerup'} />
            </div>
            <div>
              <Typography
                variant={'Subtitle-30'}
                noMargin
                className={styles['banner__header']}
                component={'div'}
              >
                {t('services::forms.refund.notification.header')}
              </Typography>
              <Typography
                variant={'Body-15'}
                className={styles['banner__text']}
                component={'div'}
              >
                {t('services::forms.refund.notification.text')}
              </Typography>
              {office_url && (
                <Button
                  target={'_blank'}
                  variant={'primary'}
                  bgColor={'Background-Primary'}
                  color={'Label-Primary'}
                  component={'a'}
                  href={office_url}
                >
                  {t('services::forms.refund.notification.button_label')}
                </Button>
              )}
            </div>
          </Container>

          <Grid
            direction={'column'}
            gap={40}
            className={'layout-tile'}
          >
            <Grid
              direction={'column'}
              gap={24}
            >
            <PageHeader>
              {t('services::forms.refund.title')}
            </PageHeader>
              <Grid
                direction={'column'}
                gap={8}
              >
                <Typography
                  variant={'Body-10'}
                  color={'Label-Secondary'}
                  noMargin
                >
                  {t('billing::account_login')}
                </Typography>
                <Grid
                  gap={8}
                  alignItems={'center'}
                  fluid={false}
                  wrap={false}
                >
                  <NetworkIcon
                    rounded
                    name={networkType}
                    size={'xs'}
                  />
                  <Typography
                    component={'div'}
                    variant={'Body-3'}
                    color={'Label-Fifth'}
                    noMargin
                  >
                    {login}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {maxRefundAmount ? (
              <RefundForm
                touchOnChange
                accountId={accountId}
                networkType={networkType}
                onClose={onClose}
                asyncValidate={validator(
                  validationSchema({
                    max_amount: maxRefundAmount,
                  }),
                )}
              />
            ) : (
              <GridSpinner />
            )}

            <Typography
              variant={'Body-15'}
              color={'Label-Fifth'}
            >
              <Trans
                i18nKey={'services::forms.refund.rules'}
                values={{ numberOfDays: networkType === 'yd' ? 10 : 3 }}
                components={{
                  1: (
                    <Button
                      color={'Label-GU-Primary'}
                      component={'span'}
                      variant={'ghost'}
                      link
                      onClick={() => {
                        dispatch({ type: FETCH_TICKETS })
                        dispatch({
                          type: PANEL_NAVIGATE,
                          payload: {
                            route: 'new_ticket',
                            params: {
                              id: null,
                              title: t('services::forms.refund.ticket_title', { login, accountId }),
                            },
                          },
                        })
                      }}
                    />
                  ),
                }}
              />
            </Typography>
          </Grid>
        </>
      )}
    </>
  )
}

export default Refund
